import React, {
  FunctionComponent,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useIntl } from 'react-intl'
import { ScrollView, View } from 'react-native'

import * as Sentry from '@sentry/react'
import { css } from 'styled-components'
import styled, { useTheme } from 'styled-components/native'

import { getAssessmentSectionHeaders } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/utils'
import { IS_PRODUCTION } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { COMMON_MIXPANEL_EVENTS } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import { AIDraftStatus } from '@lyrahealth-inc/shared-app-logic/src/models/appointmentSummary/AppointmentSummary'
import { Assignment } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import { ClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/Client'
import { SchemaProperties } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'

import { AIStatusBadge } from '../../atoms/aiStatusBadge/AIStatusBadge'
import { BodyText } from '../../atoms/bodyText/BodyText'
import { TextButton } from '../../atoms/textButton/TextButton'
import { Title } from '../../atoms/title/Title'
import { BadgeShape } from '../../badge/constants'
import { AppContext } from '../../context'
import { FormBody } from '../../organisms/formBody/FormBody'
import { TableOfContents } from '../../organisms/tableOfContents/TableOfContents'
import { TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export interface AssignmentReviewPageProps {
  clientDetails?: ClientObject | null
  onClosePress?: () => void
  renderAISummarySection?: ReactElement
  selectedAssignment: Assignment
  summaryStatus?: AIDraftStatus
  isPreferredNameEnabled?: boolean
  alertBanner?: ReactElement
  caregiverName?: string
  customCheckboxTitleComponent?: ReactElement
}

const Container = styled.View({
  flex: 1,
})

const scrollContainerCustomStyles = {
  formBodyPageContainer: css`
    max-width: 100%;
  `,
}

const HeaderContainer = styled.View<{ theme: ThemeType; backgroundColor?: string }>(
  ({ theme: { colors, spacing }, backgroundColor }) => ({
    backgroundColor: backgroundColor ?? colors.backgroundPrimary,
    padding: `${spacing['24px']} ${spacing['48px']}`,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderBottomWidth: '1px',
    borderBottomColor: colors.borderDefault,
  }),
)

const RightContainer = styled.View({
  marginTop: 'auto',
  marginBottom: 'auto',
})

const SidebarContainer = styled.View({
  top: '150px',
  left: '150px',
  position: 'fixed',
  zIndex: 1,
})

const TableOfContentsContainer = styled.View<{ theme: ThemeType }>(({ theme: { colors } }) => ({
  backgroundColor: colors.backgroundPrimary,
  padding: `16px`,
  alignItems: 'start',
  width: '225px',
  borderRadius: '16px',
  borderWidth: '1px',
  borderColor: colors.borderDefault,
}))

const ScrollContainer = styled(ScrollView)<{ displaySidebar: boolean }>(({ displaySidebar }) => ({
  marginLeft: displaySidebar ? '375px' : 0,
}))

const AssessmentContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  margin: 'auto',
  paddingTop: theme.spacing['16px'],
}))

const FormContainer = styled.View({
  width: '624px',
})

export const AssignmentReviewPage: FunctionComponent<AssignmentReviewPageProps> = ({
  clientDetails,
  onClosePress,
  renderAISummarySection,
  selectedAssignment,
  summaryStatus,
  isPreferredNameEnabled = false,
  alertBanner,
  caregiverName,
  customCheckboxTitleComponent,
}) => {
  const intl = useIntl()
  const { colors } = useTheme() as ThemeType
  const [clickedTOCHeaders, setClickedTOCHeaders] = useState<string[]>([])
  const { trackEvent } = useContext(AppContext)

  const displayAIBadge =
    summaryStatus &&
    [AIDraftStatus.COMPLETE, AIDraftStatus.IN_PROGRESS, AIDraftStatus.NOT_STARTED].includes(summaryStatus)

  const submissionDate = new Date(selectedAssignment?.assignment_responses?.[0]?.submit_date ?? '')

  const formattedSubmissionDate = intl.formatDate(submissionDate, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  })

  const submissionText = caregiverName
    ? intl.formatMessage(
        {
          defaultMessage: 'Submitted {submissionDate} by {caregiverName} (caregiver)',
          description: 'Date that completed assignment was submitted and name of the caregiver who submitted it',
        },
        { submissionDate: formattedSubmissionDate, caregiverName },
      )
    : intl.formatMessage(
        {
          defaultMessage: 'Submitted {submissionDate}',
          description: 'Date that completed assignment was submitted by client',
        },
        { submissionDate: formattedSubmissionDate },
      )

  const headers = useMemo(() => {
    const schemaProperties = selectedAssignment.content_meta_data.schema?.properties as unknown as SchemaProperties
    const headers = getAssessmentSectionHeaders({
      assignment: selectedAssignment,
      fields: schemaProperties,
      formValues: selectedAssignment.assignment_responses?.[0].response ?? [],
    })

    const mappedHeaders = headers?.map(({ content, name, aiPromptEnabled }) => ({
      elementId: name ? name : typeof content === 'string' ? content : '',
      title: typeof content === 'string' ? content : '',
      badges:
        displayAIBadge && aiPromptEnabled
          ? [<AIStatusBadge hideText key={`AIStatusBadge-${name}`} status={summaryStatus} shape={BadgeShape.RECT} />]
          : undefined,
    }))

    if (renderAISummarySection) {
      mappedHeaders.push({
        elementId: 'AssignmentReviewPage-aiSummaryContainer',
        title: 'Summary',
        badges: [
          <AIStatusBadge
            customText={intl.formatMessage({
              defaultMessage: 'AI Generated',
              description: 'Badge to let user know that the associated section is generated by AI',
            })}
            status={summaryStatus}
            key='badges-aiStatusBadge-complete'
            shape={BadgeShape.RECT}
          />,
        ],
      })
    }

    return mappedHeaders
  }, [intl, displayAIBadge, renderAISummarySection, selectedAssignment, summaryStatus])

  const sentryLogMissingUISchemaKeys = useCallback((keys: string) => {
    Sentry.captureMessage(`Missing keys in uiSchema[ui:order] --> ${keys}`, 'warning')
  }, [])

  const clickedTOCHeadersRef = useRef(clickedTOCHeaders)

  useEffect(() => {
    clickedTOCHeadersRef.current = clickedTOCHeaders
  }, [clickedTOCHeaders])

  useEffect(() => {
    return () => {
      if (clickedTOCHeadersRef.current.length > 0) {
        trackEvent?.(COMMON_MIXPANEL_EVENTS.BUTTON_PRESS, {
          action: 'select client intake toc sections',
          details: clickedTOCHeadersRef.current.toString(),
        })
      }
    }
  }, [trackEvent])

  return (
    <Container testID={tID('AssignmentReviewPage-container')}>
      <HeaderContainer testID={tID('AssignmentReviewPage-headerContainer')}>
        <View>
          <Title
            text={intl.formatMessage(
              {
                defaultMessage: '{assignmentTitle} for {clientName}',
                description: 'Title for provider view of completed assignment from client',
              },
              {
                assignmentTitle: selectedAssignment?.content?.title,
                clientName: clientDetails?.full_name,
              },
            )}
            size={TitleSize.LARGE}
            color={colors.textPrimary}
            testID={tID('AssignmentReviewPage-title')}
          />
          <BodyText
            text={submissionText}
            color={colors.textSecondary}
            testID={tID(
              caregiverName
                ? 'AssignmentReviewPage-submissionDateWithCaregiver'
                : 'AssignmentReviewPage-submissionDate',
            )}
          />
        </View>
        <RightContainer>
          {onClosePress && (
            <TextButton
              testID={tID('AssignmentReviewPage-closeButton')}
              style={{ alignSelf: 'center' }}
              onPress={onClosePress}
              text={intl.formatMessage({
                defaultMessage: 'Close',
                description: 'button text Close',
              })}
            />
          )}
        </RightContainer>
      </HeaderContainer>
      {headers.length > 0 && (
        <SidebarContainer testID={tID('AssignmentReviewPage-sidebarContainer')}>
          <TableOfContentsContainer testID={tID('AssignmentReviewPage-tableOfContentsContainer')}>
            <TableOfContents
              clickedTOCHeaders={clickedTOCHeaders}
              headers={headers}
              setClickedTOCHeaders={setClickedTOCHeaders}
            />
          </TableOfContentsContainer>
        </SidebarContainer>
      )}
      <ScrollContainer displaySidebar={headers.length > 0}>
        <AssessmentContainer testID={tID('AssignmentReviewPage-assessmentContainer')}>
          <FormContainer testID={tID('AssignmentReviewPage-formContainer')}>
            {alertBanner}
            <FormBody
              schema={selectedAssignment.content_meta_data?.schema}
              initialValues={selectedAssignment?.assignment_responses?.[0]?.response}
              uiSchema={selectedAssignment.content_meta_data?.uiSchema}
              instructions={selectedAssignment?.instructions}
              name={selectedAssignment?.content?.name}
              saveForm={() => {}}
              readOnly
              formContext={{
                externalValues: {
                  shouldSendNewWLQ: false,
                  shouldShowNewPreferredName: isPreferredNameEnabled,
                  onShowTopicDetailsPress: false,
                  shouldShowCancellationPolicyCheckbox: Boolean(customCheckboxTitleComponent),
                },
                customCheckboxTitle: customCheckboxTitleComponent,
              }}
              intl={intl}
              formButton={undefined}
              withPageBreaks={false}
              useCustomizedActivityIntroduction={
                selectedAssignment?.content_meta_data?.useCustomizedActivityIntroduction
              }
              hasNewLookAndFeel
              sentryLogMissingUISchemaKeys={IS_PRODUCTION ? sentryLogMissingUISchemaKeys : undefined}
              scrollContainerCustomStyles={scrollContainerCustomStyles}
            />
            {renderAISummarySection && (
              <View
                id={tID('AssignmentReviewPage-aiSummaryContainer')}
                testID={tID('AssignmentReviewPage-aiSummaryContainer')}
              >
                {renderAISummarySection}
              </View>
            )}
          </FormContainer>
        </AssessmentContainer>
      </ScrollContainer>
    </Container>
  )
}
